import { msHours1, routeURLs } from "../../../../../utils/enums";
import { format, addHours } from "date-fns";
import { generatePath } from "react-router-dom";
import { convertTsToLocal, addUnitsFromDate, subtractUnitsFromDate } from "../../../../../utils/displayTime";
import { isBefore } from "date-fns";
import { am4core, am4charts } from "../../../../../loaders/amchartsLoader";
import { colors, statusColors, chartPrimaryColors } from "../../../../../utils/colors";
import { charRect, dashedLineChar } from '../../../../../utils/enums';
import imgCsv from "../../../../../assets/file-csv.svg";
import { tViews } from "../../../../../utils/table";
import { initChartThemeSupport } from "../../../../../common/Theme";
import { enableScrollbarGripKeyboard, getCssVariableColor } from "../../../../../utils/chart";

export { enableScrollbarGripKeyboard };

/**
 * Description: Converts date into hours minutes and seconds without AM or PM
 * reference: https://www.iec.ch/prefixes-binary-multiples
 * @param {date} - accepted date format
 * @returns {date} formated date
 */
export const formatTime = (date) => {
    return date.format("YYYY-MM-DD HH:mm:ss")
};

/**
 * Description: Converts date into hours minutes and seconds with AM or PM
 * reference: https://www.iec.ch/prefixes-binary-multiples
 * @param {date} - accepted date format
 * @returns {date} formated date
 */
export const formatDateInAMPM = (date) => {
    return format(new Date(date), "yyyy-MM-dd, h:mm:ss a");
};

/**
 * Description: Converts text into empty space text
 * reference: https://www.iec.ch/prefixes-binary-multiples
 * @param {number} - integer and string
 * @returns {text} adding prespace * number of times
 */
export const getSpace = (num, text) => {
  for (let i = 0; i < num; i++) {
      text += `${tViews('preSpace')}`;
  }
  return text;
};

/**
 * Description: Converts time selected in a 12 hours format
 * reference: https://www.iec.ch/prefixes-binary-multiples
 * @param {timePeriod} - time period in milliseconds
 * @returns {Date} Date object
 */
export const generateDayData = (timePeriod) => {
      const data = [];
      // This getTimezoneOffset calcultes time difference in localtime and UTC in minutes
      // and multiplies it by 60 * 1000 to convert it to milliseconds.
      const timeZoneOffset = new Date().getTimezoneOffset() * 60 * 1000;
      // Use the timePeriod start time and add the offset.
      let currentHour = new Date(timePeriod + Math.abs(timeZoneOffset % msHours1));

    for (let i = 0; i < 24; i++) {
        data.push({ x: format(currentHour, "h:mm aa") }); // Format each hour as "12:00 AM", "1:00 AM", etc.
        currentHour = addHours(currentHour, 1); // Increment by 1 hour
    }

    return data;
};

/**
 * Customizes the appearance of a grip element by disabling its background and adding a vertical bar.
 *
 * @param {am4core.Container} grip - The grip element to customize.
 * @param {object} am4core - The amCharts core library object.
 * @param {object} colors - An object containing color definitions, expects a 'gray80' property.
 */
export const customizeGrip = (grip, am4core, colors) => {
    // Disable background
    grip.background.disabled = true;

    // Add vertical bar
    let line = grip.createChild(am4core.Rectangle);
    line.height = 30;
    line.width = 3;
    line.fill = colors.gray80;
    line.fillOpacity = 0.4;
    line.align = "center";
    line.valign = "middle";
};

/**
 * Generates a new array of objects with unique row IDs and selection status.
 *
 * @param {Array<Object>} res - The array of objects to process.
 * @param {boolean} isSingle - If true, marks the first row as selected; otherwise, marks the first 10 rows as not-selected.
 * @returns {Array<Object>} The processed array with added `id` and `selectedRow` properties.
 */
export const generateRowID = (res, isSingle) => {
    return res.map((item, index) => {
        let obj = {
            ...item,
            id: `row-id_${index}`
        }
        if(isSingle && index === 0) obj.selectedRow = "selected";
        if(!isSingle && index < 5) {
            obj.selectedRow = "not-selected";
        }
        return obj;
    });
};

/**
 * Transforms API response data into a format suitable for CIU chart rendering.
 *
 * @param {Array<Object>} Apires - Array of API response objects containing anomaly detection data.
 * @returns {Array<Object>} Array of objects with formatted date, anomaly flag, usage, and baseline values.
 *
 * Each returned object contains:
 *   - {string} date - Localized date string converted from entry timestamp.
 *   - {boolean|number} anomaly - Anomaly flag indicating if an anomaly was detected.
 *   - {string|number} Usage_tTip - Display value for circuit usage (for tooltip).
 *   - {string|number} BaseLine_tTip - Display value for ensemble baseline (for tooltip).
 *   - {number} Baseline - Numeric value of the ensemble baseline.
 *   - {number} Usage - Numeric value of the circuit usage.
 */
export const getCIUChartData = (Apires, timePeriod) => {
    let returnRes = [];
    Apires.forEach((item) => {
        let newObj = {
            entry_ts: convertTsToLocal(item.entry_ts),
            anomaly: item['anomaly_flag'],
            Usage_tTip: item["display_circuit_usage"],
            Baseline_tTip: item["display_ensemble_baseline"],
            Baseline: item[`ensemble_baseline`],
            Usage: item[`circuit_usage`],
        };
        returnRes.push(newObj);
    });

    addStartAndEndDates(returnRes, timePeriod);

    return returnRes;
};

/**
 * Sets the zoom range on the chart's x-axis between the specified start and end dates.
 * Disables the zoom out button and keeps the selection after zooming.
 *
 * @param {Object} chart - The chart instance to apply the zoom on.
 * @param {string|Date} endDate - The end date of the zoom range.
 * @param {string|Date} startDate - The start date of the zoom range.
 */
export const setZoom= (chart, endDate, startDate ) => {
    if(!chart.isDisposed()) {
        chart.zoomOutButton.disabled = true;
        chart.xAxes.getIndex(0).zoomToDates(
            new Date(startDate),
            new Date(endDate)
        );
        chart.xAxes.getIndex(0).keepSelection = true;
    }
};

/**
 * Calculates the scroll range for a chart based on a given hour.
 *
 * @param {string|Date} hour - The reference hour as a string or Date object.
 * @param {Object} chart - The chart object containing data.
 * @param {Array} chart.data - Array of data points, each with a `date` property.
 * @returns {{dateStart: Date, dateEnd: Date}} An object containing the calculated start and end dates for the scroll range.
 */
export const getScrollRange = (hour, chart) => {
    let dateEnd, dateStart;
    const startDate = new Date(subtractUnitsFromDate(new Date(hour), 3, "h"));
    const endDate = new Date(addUnitsFromDate(new Date(hour), 3, "h"));
    let chartEndDate = new Date(chart?.data[chart?.data?.length - 1]?.entry_ts);
    let chartStartDate = new Date(chart?.data[0]?.entry_ts);
    dateEnd = isBefore(endDate, chartEndDate) ? endDate : chartEndDate;
    dateStart = isBefore(startDate, chartStartDate) ? chartStartDate : startDate;

    return { dateStart, dateEnd };
};

/**
 * Adds grip properties to a given line object.
 *
 * Modifies the input line object by setting its height, width, fill opacity,
 * horizontal alignment, and vertical alignment.
 *
 * @param {Object} line - The line object to which grip properties will be added.
 * @returns {Object} The modified line object with grip properties applied.
 */
export const addGrip = line => {
    line.height = 30;
    line.width = 5;
    line.fillOpacity = 0.4;
    line.align = "center";
    line.valign = "middle";

    return line;
};

/**
 * Adds legends to the given chart instance, customizing their appearance and position.
 * Optionally adds an anomaly legend if specified in props.
 *
 * @param {am4charts.XYChart} chart - The chart instance to which legends will be added.
 * @param {Object} props - Properties that may include configuration for anomaly legend.
 * @param {Object} colors - An object containing color values used for legend items.
 */
export const addLegends = (chart, props, colors) => {
    chart.legend = customizeLegend(am4charts, "bottom");
    if(props.anomaly_legend) {
        chart.legend.dy = -35;
        chart.legend.paddingRight = 180;
        const anomalyLegend = new am4charts.Legend();
        anomalyLegend.data = [
            { name: tViews('predictiveNetworks.anomalyDetected'), fill: colors.gray40 }
        ];
        customizeAnomalyLegend(anomalyLegend, statusColors.poor, am4core, "bottom");
        anomalyLegend.parent = chart.chartContainer;

        // Store reference to anomaly legend for theme updates
        chart._anomalyLegend = anomalyLegend;
    }
    // Make legend items focusable
    chart.legend.itemContainers.template.focusable = true;
    initChartThemeSupport(chart, am4core, props);
};

/**
 * Creates and highlights a range on the chart's x-axis corresponding to the selected hour,
 * and adjusts the scrollbar position accordingly.
 *
 * @param {Object} props - The properties object.
 * @param {string|Date} props.selectedHour - The hour to highlight on the chart (as a date string or Date object).
 * @param {Object} chart - The chart instance containing xAxes and other chart elements.
 * @returns {*} The scroll range calculated by getScrollRange for the selected hour and chart.
 */
export const createRangeSetZoom = (props, chart) => {
    const xAxis = chart.xAxes.getIndex(0);
    if (xAxis &&
        xAxis.axisRanges &&
        xAxis.axisRanges._template &&
        !xAxis.axisRanges._template._disposed
    ) {
        xAxis.axisRanges.clear();

        //Add range background for selected hour
        let event = xAxis?.axisRanges?.create();
        event.date = new Date(props.selectedHour);
        event.endDate = new Date(addUnitsFromDate(new Date(props.selectedHour), 1, "h"));
        event.grid.disabled = true;
        event.axisFill.fillOpacity = 0.3;
        event.axisFill.fill = am4core.color(colors.blue70);
        
        // Adjust the scrollbar position according to selected hour
        let scrollRange = getScrollRange(props.selectedHour, chart);
        return scrollRange
    }
};

/**
 * Adds a downward-pointing triangle bullet to each series in the given chart, except for the series named "Baseline".
 * The triangle bullet is used to visually indicate anomalies on the chart.
 * The bullet's fill opacity is set to 1 if the corresponding data point is an anomaly, otherwise it is set to 0.
 *
 * @param {am4charts.XYChart} chart - The amCharts XYChart instance to which the triangle bullets will be added.
 */
export const addTriangleBullet = (chart, circuitName) => {
    chart.series.each(series => {
        if(((circuitName && series.name === circuitName) || !circuitName) && series.name !== "Baseline") {
            const bullet = series.bullets.push(new am4charts.Bullet());
            var arrow = bullet.createChild(am4core.Triangle);
            arrow.width = 8;
            arrow.height = 8;
            arrow.horizontalCenter = "middle";
            arrow.verticalCenter = "middle";
            arrow.strokeWidth = 0;
            arrow.fill = getCssVariableColor(statusColors.poor);
            arrow.rotation = 180;
            bullet.dy = -3;
            bullet.adapter.add("fillOpacity", function(fillOpacity, target) {
                let keys = Object.keys(target.dataItem?._dataContext);
                let key = circuitName
                    ? `${circuitName}_anomaly_flag`
                    : keys.find(k => k.includes("anomaly"));
                var isAnomaly = target.dataItem?._dataContext?.[key];
                return isAnomaly ? 1 : 0;
            });
        }
    });
};

/**
 * Applies custom styling to the given scrollbar object, including dimensions, colors, and grip visibility.
 *
 * @param {Object} scrollBar - The scrollbar object to style. Expected to have properties such as `disabled`, `minHeight`, `thumb`, `background`, `startGrip`, `endGrip`, and `chart`.
 * @returns {Object} The styled scrollbar object.
 */
export const setScrollbarStyling = (scrollBar, options = {}) => {
    scrollBar.disabled = false;
    scrollBar.minHeight = 30;
    scrollBar.thumb.minWidth = 5;
    scrollBar.background.fill = am4core.color(colors.blue70);
    scrollBar.background.fillOpacity = 0.5;
    scrollBar.startGrip.visible = true;
    scrollBar.endGrip.visible = true;
    scrollBar.startGrip.icon.disabled = true;
    scrollBar.startGrip.background.disabled = true;
    const startLine = scrollBar.startGrip.createChild(am4core.Rectangle);
    addGrip(startLine);
    scrollBar.endGrip.icon.disabled = true;
    scrollBar.endGrip.background.disabled = true;
    const endLine = scrollBar.endGrip.createChild(am4core.Rectangle);
    addGrip(endLine);
    if (scrollBar?.chart) scrollBar.chart.minHeight = 40;
    enableScrollbarGripKeyboard(scrollBar, {
        insertBeforeChart: true,
        ...options
    });

    return scrollBar;
};

/**
 * Adds a customized XY cursor to the given amCharts XY chart.
 *
 * @param {am4charts.XYChart} chart - The amCharts XY chart instance to which the cursor will be added.
 * @returns {am4charts.XYCursor} The configured XY cursor instance.
 */
export const addCursor = (chart) => {
        chart.cursor = new am4charts.XYCursor();
        chart.cursor.behavior = "none";
        chart.cursor.lineX.stroke = am4core.color(colors.gray45);
        chart.cursor.lineX.strokeWidth = 1;
        chart.cursor.lineX.strokeOpacity = 0.8;
        chart.cursor.lineY.disabled = true;
        chart.cursor.maxTooltipDistance = 0;

    return chart.cursor;
};

/**
 * Applies custom styling to an amCharts tooltip instance.
 *
 * @param {am4core.Tooltip} tooltip - The tooltip object to style.
 * @returns {am4core.Tooltip} The styled tooltip object.
 */
export const tooltipStyling = (tooltip) => {
    if(tooltip) {
        tooltip.getFillFromObject = false;
        tooltip.background.fill = am4core.color(colors.gray100);
        tooltip.label.fill = am4core.color(colors.gray15);
        tooltip.background.strokeWidth = 2;
        tooltip.background.cornerRadius = 6;
        tooltip.fontSize = "12px";
        tooltip.background.stroke = am4core.color(colors.gray15);
        tooltip.pointerOrientation = "vertical";
        tooltip.background.pointerLength = 0;

        const shadow = tooltip.background.filters.getIndex(0);
        if(shadow) {
            shadow.dy = 4;
            shadow.blur = 12;
            shadow.color = am4core.color(colors.gray0);
            shadow.opacity = 0.18;
        }
    }
    return tooltip;
};

/**
 * Updates the payload object based on the provided query parameters.
 *
 * - Adds a search term if present.
 * - Sets sorting options based on the order array, mapping display fields to their corresponding backend fields.
 * - Sets pagination options (offset and size) if page and pageLength are provided.
 * - If export is true, removes pagination and maps display fields in the fields array to backend fields.
 *
 * @param {Object} query - The query object containing search, order, page, pageLength, export, and fields.
 * @param {Object} payload - The payload object to be updated.
 * @returns {Object} The updated payload object.
 */
export const payloadOnQueryUpdate = (query, payload) => {
    if (query.search !== "") payload.search = query.search;
    if (query?.order[0]?.length > 0) {
        let sort = {}, orderFirst = query?.order[0][0], orderSecond = query?.order[0][1];
        if (orderFirst === "display_tunnel_usage") {
            sort["tunnel_usage"] = orderSecond;
        } else if (orderFirst === "display_circuit_usage") {
            sort["circuit_usage"] = orderSecond;
        } else if (orderFirst === "display_dia_usage") {
            sort["dia_usage"] = orderSecond;
        } else if (orderFirst === "display_sig_usage") {
            sort["sig_usage"] = orderSecond;
        } else if (orderFirst === "display_app_usage") {
            sort["app_usage"] = orderSecond;
        } else if (orderFirst === "display_availability") {
            sort["availability"] = orderSecond;
        } else if (orderFirst === "display_downtime") {
            sort["downtime"] = orderSecond;
        } else if (orderFirst === "display_usage") {
            sort["usage"] = orderSecond;
        } else {
            sort[orderFirst] = orderSecond;
        }
        payload["sort"] = sort;
    }
    if (query.page) payload["offset"] = query.page * query.pageLength;
    if (query.pageLength) payload["size"] = query.pageLength;
    if (query.export) {
        delete payload.size;
        delete payload.offset;
        payload.fields = payload.fields?.map(field => {
        //remove tunnel_usage and app_usage from fields as we are showing display_tunnel_usage and display_app_usage values in the export
            if (field === "display_circuit_usage") return "circuit_usage";
            if (field === "display_dia_usage") return "dia_usage";
            if (field === "display_sig_usage") return "sig_usage";
            return field;
        });
    }
    return payload
};

/**
 * Generates an array of row ID strings in the format 'row-id_{index}'.
 *
 * @param {number} [n=10] - The number of row IDs to generate. If greater than 10, only 10 IDs will be generated.
 * @returns {string[]} An array of row ID strings.
 */
export const defaultRowID = (n = 5) => {
    let m = n > 5 ? 5 : n;
    return Array.from({ length: m }, (_, i) => `row-id_${i}`);
};

/**
 * Generates heatmap data for anomaly detection visualization.
 *
 * This function prepares chart data by combining a list of time periods and circuits,
 * and augments it with additional data from the API response, such as downtime intervals.
 * The resulting array is sorted by circuit name and entry timestamp.
 *
 * @param {Array<Object>} apires - Array of API response objects, each containing at least:
 *   - {string} circuit_display_name: The display name of the circuit.
 *   - {number} entry_ts: The timestamp (in ms) when the anomaly started.
 *   - {number} downtime: The duration of downtime in milliseconds.
 * @param {Array<string|number|Date>} timePeriod - Array of time period values (timestamps or date strings).
 * @param {Array<Object>} circuitList - Array of circuit objects, each containing:
 *   - {string} name: The name of the circuit.
 * @returns {Array<Object>} Array of chart data objects, each containing:
 *   - {string} circuit_name: The name of the circuit.
 *   - {Date} local_entry_time: The entry time as a Date object.
 *   - {Date} [local_exit_time]: The exit time as a Date object (if available).
 *   - {number} [downtimeInMin]: Downtime in minutes (if available).
 *   - {string} [message]: A message describing the downtime interval (if available).
 */
export const generateHeatmapData = (apires, timePeriod, circuitList) => {
    let chartData = [];

    // This will create data for min and max time period
    timePeriod.forEach((time) => {
        circuitList.forEach(circuit => {
            let data = {}
            data["entry_ts"] = new Date(time);
            data["circuit_name"] = circuit.name;
            chartData.push(data);
        })
    })

    // This will create data for api returned response
    if(apires.length > 0) {
        apires.forEach((item) => {
            let data = {};
            const local_entry_time = convertTsToLocal(item.entry_ts);
            const downtime = item.downtime / 60000;
            const exit_time = new Date(item.entry_ts);
            exit_time.setMinutes(exit_time.getMinutes() + downtime);
            const message = `Unavailable: ${local_entry_time.toLocaleTimeString()} - ${convertTsToLocal(exit_time).toLocaleTimeString()}`;
            data["circuit_name"] = item.circuit_display_name;
            data['entry_ts'] = local_entry_time;
            data['local_exit_time'] = convertTsToLocal(exit_time);
            data["downtimeInMin"] = downtime; //downtime in minutes
            data["message"] = message;
            chartData.push(data);
        });
    }

    chartData.sort((a, b) => {
        if (a.circuit_name < b.circuit_name) return -1;
        if (a.circuit_name > b.circuit_name) return 1;
        // If circuit_name is the same, sort by entry_ts
        return convertTsToLocal(a.entry_ts) - convertTsToLocal(b.entry_ts);
    });

    return chartData;
};

/**
 * Calculates the start and end range for a location based on the circuit length.
 *
 * @param {number} circuitLength - The length of the circuit.
 * @returns {[number, number]} An array containing the start and end range values.
 */
export const getLocationRange = circuitLength => {
  let start = 0.4,
    end = 0.6;
  if (circuitLength === 10) {
    return [start, end];
  } else {
    let customSpace = (10 - circuitLength) * 0.01;
    return [start + customSpace, end - customSpace];
  }
};

/**
 * Generates a dynamic object with keys based on the circuit name from the given item.
 *
 * @param {Object} item - The item containing circuit information.
 * @param {string} item.circuit_name - The name of the circuit.
 * @param {*} item.anomaly_flag - The anomaly flag value for the circuit.
 * @param {*} item.circuit_usage - The usage value for the circuit.
 * @param {*} item.display_circuit_usage - The display usage value for the circuit.
 * @returns {Object} An object with dynamically generated keys for anomaly flag, usage, and tooltip based on the circuit name.
 */
export const getDynamicObj = (item, entity) => {
    if(entity === "apps") {
        return {
            [`${item.application}_usage`]: item.usage,
            [`${item.application}_tTip`]: item.display_usage
        };
    } else if(entity === "circuits") {
        return {
            [`${item.display_circuit_name}_anomaly_flag`]: item.anomaly_flag,
            [`${item.display_circuit_name}_usage`]: item.circuit_usage,
            [`${item.display_circuit_name}_tTip`]: item.display_circuit_usage,
        };
    }
}

/**
 * Processes the API response data and returns an array of objects formatted for charting.
 * For each unique timestamp (`entry_ts`), creates or updates an entry in the processed data.
 * Merges dynamic properties from each item using `getDynamicObj`.
 *
 * @param {Array<Object>} apiRes - The API response array, where each object contains at least an `entry_ts` property.
 * @returns {Array<Object>} An array of processed data objects, each with a `date` property (as a Date object) and dynamic properties.
 */
export const getProcessedChartData = (apiRes, entity, timePeriod) => {
    let processedData = [], colorPalette = [], colorsAssign = [...chartPrimaryColors];
    let defaultIndex = colorsAssign.indexOf(colors.teal60);
    if(defaultIndex > -1) colorsAssign.splice(defaultIndex, 1);
    // Process the API response and return the processed data
    apiRes.forEach(item => {
        const exists = processedData.find(existing => new Date(existing.entry_ts).getTime() === new Date(convertTsToLocal(item.entry_ts)).getTime());
        if (!exists) {
            let entryObj = {}
            entryObj.entry_ts = convertTsToLocal(item.entry_ts);
            Object.assign(entryObj, getDynamicObj(item, entity));
            processedData.push(entryObj);
        } else {
            Object.assign(exists, getDynamicObj(item, entity));
        }

        if(colorsAssign.length > 0) {
            const colorExists = colorPalette.find(existing => entity === "apps" ? existing.name === item.application : existing.name === item.display_circuit_name);
            if (!colorExists) {
                colorPalette.push({
                    name: entity === "apps" ? item.application : item.display_circuit_name,
                    color: colorsAssign[0]
                });
                colorsAssign.shift();
            }
        }
    });

    addStartAndEndDates(processedData, timePeriod);

    return {data: processedData, palette: colorPalette};
}

export const getMetricSeriesName = (field = "") => {
    return field.replace(/_(anomaly_flag|tTip|usage)$/, "");
}

/**
 * Generates a tooltip string by iterating over the keys of a data item and appending formatted tooltip information
 * for each key that includes "tTip". The tooltip includes a symbol, label, and value, formatted with colors and spacing.
 *
 * @param {string} tTip - The initial tooltip string to append to.
 * @param {Object} dataItem - The data object containing tooltip information.
 * @param {Object} tViews - The internationalization object, expected to have a `preSpace` property.
 * @returns {string} The formatted tooltip string with appended information.
 */
export const genericTtip = (tTip, dataItem, tViews, palette, circuitName, chart) => {
    chart.series.values.forEach(ser => {
        let tipList = dataItem && Object.keys(dataItem);
        tipList?.forEach((item) => {
            let label = getMetricSeriesName(item);
            let seriesLabel = getMetricSeriesName(ser.dataFields.valueY);
            if(item.includes("tTip") && seriesLabel === label) {
                let color = circuitName && circuitName === label
                    ? colors.teal60 : getSeriesColor(label, palette)?.[0]?.color;
                let symbol = label === "BaseLine" ? dashedLineChar : charRect;
                tTip += `[${color}]${symbol}[/]${getSpace(4, '')}${label}: ${tViews('preSpace')} ${getSpace(6, '')}${dataItem[item]}\n\n`;
            }
        })
        
    })
    return tTip;
}

/**
 * Determines whether a re-render is required for the chart based on the changes in the selected circuits.
 *
 * @param {string[]} prev - The previous list of selected circuit names.
 * @param {Object[]} current - The current list of selected circuit objects, each containing a `name` property.
 * @param {Function} updatePrev - Function to update the previous selection state.
 * @param {Function} setNoData - Function to set the "no data" state.
 * @param {Function} setRemoveList - Function to update the list of circuits to be removed.
 * @param {string[]} removedList - List of circuit names that were removed in the previous state.
 * @param {Function} setAddItem - Function to update the list of circuits to be added.
 * @param {string[]} apiCircuitList - The complete list of circuit names from the API.
 * @returns {boolean} Returns true if a re-render is required, otherwise false.
 */
export const isRenderRequired = (prev, current, updatePrev, setNoData, setRemoveList, removedList, setAddItem, apiCircuitList, setAPIAppList) => {
    if(apiCircuitList.length === 0 && ((prev.length === 0 && current.length > 0) ||
        (!prev.every(item => current.includes(item))))) {
        updatePrev(current);
        setRemoveList([]);
        setAddItem([]);
        return true; // Render the chart if API circuit list is empty including initial render
    }

    // current list is missing in API circuit list, fetch new data
    if(current.length > 0 && apiCircuitList.length > 0 && !current.every(item => apiCircuitList.includes(item))) {
        updatePrev(current);
        setRemoveList([]);
        setAddItem([]);
        setAPIAppList([]);
        return true;
    }

    if(apiCircuitList.length === 0 && !prev.every(item => current.includes(item))) {
        // If API circuit list is empty and current selection is different from previous
        updatePrev(current);
        setRemoveList([]);
        setAddItem([]);
        return true; // Render the chart if API circuit list is empty and selection has changed

    }

    // Enter only current not equal to prev
    if(!prev.every(item => current.includes(item)) ||
        (current.length > 0 && prev.length === 0)) {
        if(current.length === 0 && prev.length > 0) { // All De-select case
            setNoData(true);
            setRemoveList(apiCircuitList); // All circuits are removed
            setAddItem([]);
        } else if(current.length > 0 && prev.length === 0 &&
            current.length === apiCircuitList.length) { // All Select case
                setNoData(null);
                setRemoveList([]);
        } else { // Some circuits are selected and some are deselected
            setNoData(null);
            if(removedList.length > 0) { // Check for addition
                let addItem = removedList.filter(item => {if(current.includes(item)) return item});
                setAddItem(addItem);
                setRemoveList(old => old.filter(item => !addItem.includes(item)));
            } else setAddItem([]);
            let removedCircuits = apiCircuitList.filter(elem => {if(!current.includes(elem)) return elem});
            setRemoveList(removedCircuits);
        }
        updatePrev(current);
    } else {
        // when prev and current are same check if current is empty and set no data
        current.length === 0 && prev.length === 0 && apiCircuitList.length > 0 ? setNoData(true) : setNoData(null);
        if(removedList.length > 0) { // Check for addition
            let addItem = removedList.filter(item => {if(current.includes(item)) return item});
            if(addItem.length > 0) {
                setAddItem(addItem);
                setRemoveList(old => old.filter(item => !addItem.includes(item)));
                updatePrev(current);
            }
        } else setAddItem([]);
    }
    return false; // Do not render the chart
}


/**
 * Returns the color from the palette that matches the given field.
 *
 * @param {string} field - The field name, expected to contain an underscore (e.g., "name_value").
 * @param {Array<{name: string, color: string}>} palette - An array of palette objects with 'name' and 'color' properties.
 * @returns {Array} An array of palette entries where the name matches the prefix of the field, with the color processed by am4core.color.
 */
export const getSeriesColor  = (field, palette) => {
    if(field === "BaseLine") {
        return [{color: colors.gray15}];
    } else if(field === "Usage") {
        return [{color: colors.teal60}];
    } else {
        return palette?.filter(p => {
            if(p.name === field) {
                return am4core.color(p.color);
            }
        })
    }
}

/**
 * Toggles the visibility of the sites dropdown widget based on the selected tab.
 *
 * @param {string} tab - The currently selected tab. If the value is "usageTab", the widget is hidden; otherwise, it is shown.
 * @returns {HTMLElement|null} The DOM element representing the sites dropdown widget, or null if not found.
 */
export const sitesWidgetVisibility = (tab) => {
    const siteWidget = document.querySelector("[data-cy=site-network-hierarchy]");
    if(siteWidget) {
        if(tab === "sitesTab") {
            siteWidget.style.visibility = "visible";
        } else {
            siteWidget.style.visibility = "hidden";
        }
    }
    return siteWidget;
}

/**
 * Renders a selectable table cell with a checkbox for row selection.
 *
 * @param {Function} createElement - Function to create a new DOM element.
 * @param {Object} props - Properties for the table cell.
 * @param {Object} props.model - The data model for the row.
 * @param {string|number} props.model.id - Unique identifier for the row.
 * @param {number} props.rowIndex - Index of the row in the table.
 * @param {Object} store - Store object containing state and hooks.
 * @param {Function} store.getHook - Function to retrieve hooks by name.
 * @param {Function} store.getState - Function to retrieve the current state.
 * @returns {Object} The created checkbox element for the table cell.
 */
export const tableCellSelection = (createElement, props, store) => {
    const uniqueId = props.model.id;
    const selectableRowsHook = store.getHook("selectableRows");
    const { selected } = store.getState();
    return createElement("hbr-checkbox", {
        checked: selected.includes(uniqueId) ? true : undefined,
        "data-rowIndex": props.rowIndex,
        "data-testid": uniqueId,
        intermediate: false,
        disabled: selected.length >= 5 && !selected.includes(uniqueId),
        "onHbr-click": event => {
            event.target.blur();
            const { selected: current } = store.getState();
            const selections = [...current];
            const selectionIndex = selections.indexOf(uniqueId);
            if (selectionIndex === -1) selections.push(uniqueId);
            else selections.splice(selectionIndex, 1);
            selectableRowsHook.setSelected(selections);
        }
    });
}

/**
 * Creates a table column selection checkbox element for selecting rows in a table.
 *
 * The checkbox supports indeterminate state when some but not all rows are selected,
 * and disables itself when there are no rows to select. When checked, it selects up to
 * 5 rows from the source; when unchecked, it clears the selection.
 *
 * @param {Function} createElement - Function to create a UI element (e.g., a virtual DOM node).
 * @param {Object} store - Store object containing state and hooks for row selection.
 * @returns {Array} An array containing the checkbox element for table column selection.
 */
export const tableColumnSelection = (createElement, store) => {
    const selectableRowsHook = store.getHook("selectableRows");
    const { source, selected } = store.getState();
    return [createElement("hbr-checkbox", {
        indeterminate: selected.length > 0 && selected.length < 5,
        checked: selected.length === 5,
        disabled: source.length === 0,
        style: { margin: "var(--cell-padding)" },
        "data-testid": "checkbox-header",
        "onHbr-click": event => {
            event.target.blur();
            const checked = event.target.checked;
            const selections =
                checked === true ? source.slice(0, 5).map(item => item.id) : [];
            selectableRowsHook.setSelected(selections);
        }
    })];
}

/**
 * Handles the selection of a site by storing site details in localStorage,
 * updating the selected site state, and navigating to the anomaly detection page for the selected site.
 *
 * @param {Object} rowData - The data object representing the selected site.
 * @param {string} rowData.site_name - The name of the selected site.
 * @param {string} rowData.city - The city/location of the selected site.
 * @param {string|number} rowData.site_id - The unique identifier of the selected site.
 * @param {Function} navigate - React Router navigate function.
 * @param {Function} setSelectedSite - Function to update the selected site state.
 */
export const siteSelection = (rowData, navigate, setSelectedSite) => {
    localStorage.setItem("local_details", JSON.stringify(
      { "local_site": rowData.site_name ,
        "local_location": rowData.city,
      }));
    setSelectedSite(rowData.site_id);
    navigate(generatePath(routeURLs.adSingleSite, { siteName: rowData.site_name }));
}

export const convertToCSV = (arr, name) => {
    const csv = [];
    const headers = arr[0].data.map((item) => item.x);

    // Get the headers from the first object
    csv.push(` ,${headers.join(",")}`);

    // Iterate over each object and convert it to a CSV row
    arr.forEach((obj) => {
        const row = [];
        headers.forEach((header) => {
            const target = obj.data.find((r) => r.x === header);
            if (target) {
                let yVal = target.y !== undefined ? `${tViews('anomalyRatewithColon')} ${target.y}%` : '';
                let usgVal = target.usage !== undefined ? `${tViews('usagewithColon')} ${target.usage}` : '';
                row.push(`${yVal} ${usgVal}`);
            }
        });
        csv.push(`${obj.id},${row.join(",")}`);
    });

    browserDownloadCsv(csv.join("\n"), name);
}

export const browserDownloadCsv = (resultCsv, filename) => {
	const blob = new Blob([resultCsv], { type: "text/csv" });
	const link = document.createElement("a");
	link.href = URL.createObjectURL(blob);
	link.download = filename;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
};

/**
 * Handles the click event for the CSV download button.
 * Finds the closest parent <hbr-heatmap-chart> element and triggers its download method
 * with the specified filename.
 * For harbor heatmap charts, this will download the chart data as a CSV file.
 * @param {Event} event - The click event triggered by the user.
 * @param {string} name - The base name to use for the downloaded CSV file.
 */
export const onClickCsvBtn = (event, name) => {
    event.target.closest("hbr-heatmap-chart").download(`${name}.csv`);
};

/**
 * Adds export functionality to the given chart instance.
 * Configures the export menu to allow CSV export with a custom icon and file prefix.
 * For am4charts
 * @param {am4charts.Chart} chart - The chart instance to which export options will be added.
 */
export const addExport = (chart, id) => {
        // Export menu option
        chart.exporting.menu = new am4core.ExportMenu();
        chart.exporting.dataSource = chart.data;
        chart.exporting.filePrefix  = id;
        chart.exporting.menu.items = [{ type: 'csv', icon: imgCsv }];
};

/**
 * Ensures that the result array contains objects with dates matching the start and end of the given time period.
 * If the start or end date is missing, it adds an object with the corresponding date at the beginning or end of the array.
 *
 * @param {Array<{date: Date}>} res - The array of objects, each containing a `date` property as a Date object.
 * @param {[number, number]} timePeriod - An array with two timestamps [start, end] representing the time period.
 * @returns {Array<{date: Date}>} The updated array with ensured start and end date objects.
 */
export const addStartAndEndDates = (res, timePeriod) => {
    const startExists = res.find(item => item.entry_ts.getTime() === new Date(timePeriod[0]).getTime());
    if (!startExists) {
        res.unshift({
            entry_ts: new Date(timePeriod[0])
        });
    }
    const endExists = res.find(item => item.entry_ts.getTime() === new Date(timePeriod[1]).getTime());
    if (!endExists) {
        res.push({
            entry_ts: new Date(timePeriod[1])
        });
    }

    return res;
}

/**
 * Returns the provided usageTunnel value if it exists, otherwise returns 0.
 *
 * @param {*} usageTunnel - The usage tunnel value to check.
 * @returns {*} The usageTunnel value if truthy, otherwise 0.
 */
export const topTunnel = (usageTunnel) => {
  return usageTunnel ? usageTunnel : 0
}

/**
 * Customizes the appearance and behavior of an amCharts legend for anomaly detection.
 *
 * - Disables click and focus on legend items.
 * - Sets the cursor style to default.
 * - Adjusts marker size and alignment.
 * - Adds a colored triangle marker to the legend.
 * - Positions the legend based on the provided position parameter.
 *
 * @param {am4charts.Legend} legend - The legend instance to customize.
 * @param {am4core.Color} color - The color to use for the triangle marker.
 * @param {typeof am4core} am4core - The amCharts core module.
 * @param {string} [position="top"] - The position of the legend ("top", "bottom", etc.).
 */
export const customizeAnomalyLegend = (legend, color, am4core, position = "top") => {
  legend.itemContainers.template.clickable = false;
  legend.itemContainers.template.focusable = false;
  legend.itemContainers.template.cursorOverStyle =
      am4core.MouseCursorStyle.default;
  var marker = legend.markers.template;
  marker.width = 0;
  marker.height = 0;
  legend.contentAlign = "right";
  legend.userClassName = "hbr-type-body2"
  if(position === "top") {
    legend.dx = -220;
    legend.dy = -200;
  }
  legend.position = position;
  let triangle = marker.createChild(am4core.Triangle);
  triangle.width = 10;
  triangle.height = 10;
  triangle.rotation = 180;
  triangle.valign = "middle";
  triangle.dy = -5;
  triangle.fill = getCssVariableColor(color);
}

/**
 * Customizes and returns an amCharts legend with specific styles and positioning.
 *
 * @param {typeof import("@amcharts/amcharts4/charts")} am4charts - The amCharts charts module.
 * @param {string} position - The position of the legend (e.g., "right", "left", "top", "bottom").
 * @returns {am4charts.Legend} The customized legend instance.
 */
export const customizeLegend = (am4charts, position) => {
  let legend = new am4charts.Legend();
  legend.userClassName = "hbr-type-body2";
  legend.contentAlign = "right";
  legend.position = position;
  legend.paddingBottom = 8;
  legend.useDefaultMarker = false;
  legend.markers.template.width = 20;
  legend.markers.template.height = 20;
  const marker = legend.markers.template.children.getIndex(0);
  marker.cornerRadius(0, 0, 0, 0);
  marker.width = 20;
  marker.height = 3;
  marker.valign = "middle";

  // Disable click and focus actions
  legend.itemContainers.template.clickable = false;
  legend.itemContainers.template.focusable = false;
  legend.itemContainers.template.cursorOverStyle = am4core.MouseCursorStyle.default;

  return legend
};
